/**
 * Per-user runtime-session transcript hook.
 *
 * Mirrors `useSessionTranscript` but hits `/agent/my-sessions/:id/`
 * (non-staff, owner-scoped) and adds optional polling so an in-flight
 * sub-agent's transcript animates as new events land. Used by the brand
 * wizard's inline sub-agent view: a non-admin owner clicks a `dispatch_*`
 * tool card and sees the child session as it streams.
 */

import { useEffect, useRef, useState } from "react";
import type { UIMessage } from "@ai-sdk/react";
import { z } from "zod";

import { apiFetch } from "@/lib/jwt-auth-client";
import { eventsToUIMessages } from "@/hooks/useSessionTranscript";

const RuntimeEventSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  event_type: z.string(),
  data: z.record(z.string(), z.unknown()),
});

const MySessionDetailSchema = z.object({
  session: z
    .object({
      id: z.string(),
      agent_name: z.string(),
      runtime: z.string(),
      status: z.string(),
      title: z.string().optional(),
    })
    .passthrough(),
  events: z.array(RuntimeEventSchema),
  events_truncated: z.boolean(),
  max_sequence: z.number(),
});

export interface UseMyRuntimeSessionResult {
  messages: UIMessage[] | undefined;
  agentName: string | undefined;
  runtime: string | undefined;
  status: string | undefined;
  loading: boolean;
  error: string | undefined;
  /** Force an immediate refetch (e.g. after the user types). */
  refetch: () => void;
}

const TERMINAL_STATUSES = new Set(["idle", "completed", "errored", "terminated"]);

export function useMyRuntimeSession(
  sessionId: string | undefined,
  options?: { pollMs?: number },
): UseMyRuntimeSessionResult {
  const pollMs = options?.pollMs ?? 2500;
  const [messages, setMessages] = useState<UIMessage[] | undefined>(undefined);
  const [agentName, setAgentName] = useState<string | undefined>(undefined);
  const [runtime, setRuntime] = useState<string | undefined>(undefined);
  const [sessionStatus, setSessionStatus] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const tickRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setMessages(undefined);
      setAgentName(undefined);
      setRuntime(undefined);
      setSessionStatus(undefined);
      setError(undefined);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchOnce = async (isInitial: boolean) => {
      if (isInitial) setLoading(true);
      try {
        const res = await apiFetch(`/agent/my-sessions/${sessionId}/`);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const raw = await res.json();
        if (cancelled) return;
        const parsed = MySessionDetailSchema.parse(raw);
        setAgentName(parsed.session.agent_name);
        setRuntime(parsed.session.runtime);
        setSessionStatus(parsed.session.status);
        setMessages(eventsToUIMessages(parsed.events));
        setError(undefined);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled && isInitial) setLoading(false);
      }
    };

    const schedule = (isInitial: boolean) => {
      void fetchOnce(isInitial).then(() => {
        if (cancelled) return;
        // Stop polling once the session reaches a terminal status.
        if (sessionStatus && TERMINAL_STATUSES.has(sessionStatus)) return;
        timer = setTimeout(() => schedule(false), pollMs);
      });
    };

    schedule(true);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, pollMs, tickRef.current]);

  const refetch = () => {
    tickRef.current += 1;
  };

  return {
    messages,
    agentName,
    runtime,
    status: sessionStatus,
    loading,
    error,
    refetch,
  };
}
